import {Box, Button, FormControlLabel, SelectChangeEvent, Stack, Switch, Typography} from "@mui/material";
import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {
    CONFIG_DEFINITIONS,
    ConfigDefinition,
    ConfigurationEntry,
    FEATURE_ACCESS_CONFIG_DEFINITIONS,
    FEATURE_ACCESS_CONFIG_ORDER,
    FEATURE_POINTER_KEY,
    Model,
} from "../../../../../models/admin-config.models";
import ModelConfiguration from "../ModelConfiguration";

import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import CircularProgress from "@mui/material/CircularProgress";
import {parseError} from "../../../../../errors/http.service";
import {adminConfigurationService} from "../../../../../services/admin-config.service";
import {env} from "../../../../../config/environment";
import {useFeatureConfig} from "../../../../../contexts/FeatureConfigContext";

const GeneralConfiguration = () => {
    const {t} = useTranslation();
    const { showSnackbar } = useSnackbar();
    // TODO: loading features
    const { featureEntries, saveFeatureConfig } = useFeatureConfig();

    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [draftFeatureEntries, setDraftFeatureEntries] = useState<ConfigurationEntry[]>([]);

    const [defaultEntries, setDefaultEntries] = useState<ConfigurationEntry[]>([]);
    const [defaultModel, setDefaultModel] = useState<Model | null>(null);
    const [availableModels, setAvailableModels] = useState<Model[]>([]);

    const { clientTheme } = env;
    const systemName = t(`brand.${clientTheme}.productName`);

    useEffect(() => {
        setDraftFeatureEntries(featureEntries);
    }, [featureEntries]);

    const loadConfig = useCallback(async () => {
        setIsFetching(true);

        try {
            const [data, model, modelsList] = await Promise.all([
                adminConfigurationService.fetchGeneralConfig(),
                adminConfigurationService.getDefaultModelConfig(),
                adminConfigurationService.getModelsList(),
            ]);

            const defaultData = data
                .filter(entry => CONFIG_DEFINITIONS[entry.propertyName])
                .sort((a, b) => a.propertyName.localeCompare(b.propertyName));

            setDefaultEntries(defaultData);
            setDefaultModel(model);
            setAvailableModels(modelsList);
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.loadError'), 'error');
        } finally {
            setIsFetching(false);
        }
    }, [t]);

    useEffect(() => {
        void loadConfig();
    }, []);

    const handleBooleanToggle = async (name: string, checked: boolean) => {
        const newValue = checked ? 'true' : 'false';

        if (name.includes(FEATURE_POINTER_KEY)) {
            setDraftFeatureEntries(prev =>
                prev.map(e => (e.propertyName === name ? { ...e, propertyValue: newValue } : e)),
            );
        } else {
            setDefaultEntries(prev =>
                prev.map(e => (e.propertyName === name ? { ...e, propertyValue: newValue } : e)),
            );
        }
    };

    const handleModelChange = async (e: SelectChangeEvent<number>): Promise<void> => {
        const newModelId = Number(e.target.value);
        const newModel = availableModels.find(m => m.id === newModelId);

        if (!newModel) {
            console.error('Chosen model not found')
            showSnackbar(t('configuration.modelNotFound'), 'error');
            return;
        }

        setDefaultModel(newModel);
    };

    const handleSaveAll = async () => {
        try {
            setIsSaving(true);

            await Promise.all([
                ...defaultEntries.map(e => adminConfigurationService.setConfig(e.propertyName, e.propertyValue)),
                saveFeatureConfig(draftFeatureEntries),
                defaultModel?.id ? adminConfigurationService.setDefaultModelConfig(defaultModel.id) : Promise.resolve(),
            ]);

            showSnackbar(t('configuration.updateSuccess'), 'success');

            await loadConfig();
        } catch (err) {
            console.error(err);
            showSnackbar(parseError(err) || t('configuration.updateError'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderToggleElement = (entry: ConfigurationEntry, definitions: Record<string, ConfigDefinition>) => {
        const definition = definitions[entry.propertyName];

        if (!definition) {
            return null;
        }

        if (definition.type === 'boolean') {
            const checked = entry.propertyValue === 'true';

            return (
                <Box key={entry.propertyName} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                        <FormControlLabel
                            label={t(definition.labelKey, { systemName })}
                            control={
                                <Switch
                                    checked={checked}
                                    onChange={(_, newChecked) =>
                                        handleBooleanToggle(
                                            entry.propertyName,
                                            newChecked,
                                        )
                                    }
                                    inputProps={{
                                        'aria-describedby': definition.descriptionKey
                                            ? `${entry.propertyName}-desc`
                                            : undefined,
                                    }}
                                />
                            }
                        />
                    </Box>

                    {definition.descriptionKey && (
                        <Typography
                            id={`${entry.propertyName}-desc`}
                            variant="body2"
                            color="text.secondary"
                            sx={{ml: 6}}
                        >
                            {t(definition.descriptionKey, { systemName })}
                        </Typography>
                    )}
                </Box>
            )
        }

        return null;
    };


    return (
        <Stack direction='column' spacing={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {defaultEntries.map(entry => renderToggleElement(entry, CONFIG_DEFINITIONS))}

                {!!defaultModel && (
                    <ModelConfiguration
                        availableModels={availableModels}
                        defaultModel={defaultModel}
                        handleModelChange={handleModelChange}
                    />
                )}

                <Typography variant="h6" color="text.primary" sx={{ mt: 2 }}>
                    {t('configuration.accessConfigLabel') + ':'}
                </Typography>

                {draftFeatureEntries.map(entry => renderToggleElement(entry, FEATURE_ACCESS_CONFIG_DEFINITIONS))}
            </Box>

            <Box sx={{ alignSelf: 'flex-end' }}>
                <Button variant="contained" disabled={isFetching || isSaving} onClick={handleSaveAll}>
                    {isSaving ? <CircularProgress size={16} /> : t('common.save')}
                </Button>
            </Box>
        </Stack>
    );
};

export default GeneralConfiguration;